import { useTranslation } from "react-i18next";
import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { PerformanceTile } from "../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import { PVRequirementTile } from "../../PerformanceTile/PVRequirementTile/PVRequirementTile";
import { PinRequirement } from "../../PerformanceTile/PinRequirement/PinRequirement";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActionCardError } from "../../../GroupManagementDashboard/ActionCard/ActionCardError/ActionCardError";
import { useAppSelector } from "../../../../../store";
import { getCanonicalTitle } from "../../../../../common/util/Incentives/IncentiveRegistry";
import { ROUTE_PATHS } from "../../../../../common/enums/routing-enums";
import { QUALIFICATION_STATUS } from "../../../../../common/enums/incentive-enums";
import defaultContent from "../../../../../content/performanceIncomeDashboard";
import lsiDefaultContent from "../../../../../content/leadershipSeminar";
import type {
  LSPathEntry,
  LSPathRequirementsMap,
} from "../../../../../services/LeadershipSeminar/leadershipSeminarAPI.types";
import { FetchLeadershipSeminarPathViewData } from "./LeadershipSeminarPathViewDataLayer";
import { parseStatus, parseBadgeStatus } from "../utils";
import type { JSX } from "react";

const findRequirement = <T extends { Name: string }>(arr: T[] | undefined, name: string): T | undefined =>
  arr?.find((r) => r.Name === name);

const percentOf = (current: number, target: number): number =>
  target > 0 ? Math.min((current / target) * 100, 100) : 0;

/**
 * Annual Performance & Income tile for the Leadership Seminar path view (e.g. Japan).
 * Displays the default qualification path's requirements and overall progress/status,
 * driven by the LSI path view overview API. Mirrors the layout of LeadershipTripQualTile
 * (left-aligned donut/shield requirement rows).
 */
export const LeadershipSeminarPathViewTile = () => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { user } = useAppSelector((state) => state.boot);
  const { lsPathViewData, isLoading, errorStatus, refetch } = FetchLeadershipSeminarPathViewData(selectedPeriod);
  const { t } = useTranslation(["performanceIncomeDashboard", "leadershipSeminar", "navigation", "awardLabels"]);
  const pageTitle = getCanonicalTitle({ id: "leadershipSeminar" }, t, { aff: user.aff });

  const getBadgeTitle = (reqName: string): string => {
    const badges = lsiDefaultContent.pathView.badges as Record<string, { title: string }>;
    const fallback = badges[reqName]?.title ?? reqName;
    return t(`pathView.badges.${reqName}.title`, fallback, { ns: "leadershipSeminar" }).toString();
  };

  const getSummaryLabel = (reqName: string, params: Record<string, string>): string => {
    const pathView = lsiDefaultContent.pathView as Record<string, unknown>;
    const fallback = typeof pathView[reqName] === "string" ? (pathView[reqName] as string) : reqName;
    return t(`pathView.${reqName}`, fallback, { ns: "leadershipSeminar", ...params }).toString();
  };

  const renderBadge = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.Badge, reqName);
    if (!data) return null;
    return (
      <PinRequirement
        key={reqName}
        met={data.Met}
        status={parseBadgeStatus(data.Status)}
        label={getBadgeTitle(reqName)}
        value=""
      />
    );
  };

  const getRequalifiedBadgeTitle = (): string => {
    const pathView = lsiDefaultContent.pathView as Record<string, string>;
    // Always show the full requirement title, matching the path label and the
    // Leadership Seminar page. Never fall back to the raw API requirement name
    // (derivedNewOrRequalifiedAward).
    return t("pathView.firstTimeTitle", pathView.firstTimeTitle, { ns: "leadershipSeminar" }).toString();
  };

  const renderRequalifiedBadge = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.RequalifiedBadge, reqName);
    if (!data) return null;
    return (
      <PinRequirement
        key={reqName}
        met={data.Met}
        status={parseBadgeStatus(data.Status)}
        label={getRequalifiedBadgeTitle()}
        value=""
      />
    );
  };

  const renderVolume = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.Volume, reqName);
    if (!data) return null;
    const label = `${data.Volume.Current.Formatted}/${data.Volume.Target.Formatted} ${getSummaryLabel(reqName, {
      volume: data.Volume.Target.Formatted,
    })}`;
    return (
      <PVRequirementTile
        key={reqName}
        percent={percentOf(data.Volume.Current.Actual, data.Volume.Target.Actual)}
        met={data.Met}
        label={label}
      />
    );
  };

  const renderNewLeg = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.NewLeg, reqName);
    if (!data) return null;
    return (
      <PVRequirementTile
        key={reqName}
        percent={percentOf(data.Current, data.Target)}
        met={data.Met}
        label={getSummaryLabel(reqName, { target: String(data.Target) })}
      />
    );
  };

  const renderQCount = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.QCount, reqName);
    if (!data) return null;
    return (
      <PVRequirementTile
        key={reqName}
        percent={percentOf(data.YTDQCount, data.Target)}
        met={data.Met}
        label={getSummaryLabel(reqName, { target: String(data.Target), ytdQCount: String(data.YTDQCount) })}
      />
    );
  };

  const renderMaintainFQOrGrowGPV = (reqName: string, map: LSPathRequirementsMap): JSX.Element | null => {
    const data = findRequirement(map.MaintainFQOrGrowGPV, reqName);
    if (!data) return null;
    const met = data.Grow.Met || data.Maintain.Met;
    return (
      <PVRequirementTile
        key={reqName}
        percent={met ? 100 : 0}
        met={met}
        label={getSummaryLabel(reqName, { target: "1" })}
      />
    );
  };

  const renderRequirement = (reqName: string, reqType: string, map: LSPathRequirementsMap): JSX.Element | null => {
    switch (reqType) {
      case "Badge":
        return renderBadge(reqName, map);
      case "RequalifiedBadge":
        return renderRequalifiedBadge(reqName, map);
      case "Volume":
        return renderVolume(reqName, map);
      case "NewLeg":
        return renderNewLeg(reqName, map);
      case "QCount":
        return renderQCount(reqName, map);
      case "MaintainFQOrGrowGPV":
        return renderMaintainFQOrGrowGPV(reqName, map);
      default:
        return null;
    }
  };

  const renderDefaultPath = (path: LSPathEntry, map: LSPathRequirementsMap): JSX.Element[] => {
    const orderedNames = path.ViewLayout.Row.flat();
    return orderedNames
      .map((reqName) => {
        const pathReq = path.Requirements.find((r) => r.ReqName === reqName);
        return pathReq ? renderRequirement(reqName, pathReq.ReqType, map) : null;
      })
      .filter((tile): tile is JSX.Element => tile !== null);
  };

  if (isLoading) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
        <LeafRectangleLoader isLoading width="100%" height="2rem" margin="0" />
        <LeafRectangleLoader isLoading width="100%" height="2rem" margin="0" />
      </PerformanceTile>
    );
  }

  if (errorStatus) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader link={ROUTE_PATHS.LEADERSHIP_SEMINAR} label={pageTitle} />
        <ActionCardError onRetry={refetch} />
      </PerformanceTile>
    );
  }

  const comingSoonBlock = () => (
    <div className="AnnualPerformanceIncome__comingSoon">
      <p className="AnnualPerformanceIncome__comingSoon-title">
        {String(t("incentives.comingSoon.title", defaultContent.incentives.comingSoon.title))}
      </p>
      <p className="AnnualPerformanceIncome__comingSoon-message">
        {String(t("incentives.comingSoon.message", defaultContent.incentives.comingSoon.message))}
      </p>
    </div>
  );

  // Statuses that replace the requirement rows with a single banner message
  // (e.g. "Congratulations! You have been manually approved for {title}...").
  // The API can signal approval two ways: an explicit approved Status, or a
  // QUALIFIED/other Status combined with the AutomaticallyApproved flag — both
  // must surface the congratulations message.
  const getMessageKey = (status: string, automaticallyApproved: boolean): string => {
    if (automaticallyApproved) {
      return "manuallyApprovedMessage";
    }
    switch (status) {
      case QUALIFICATION_STATUS.HELD:
        return "onHoldMessage";
      case QUALIFICATION_STATUS.DENIED:
      case QUALIFICATION_STATUS.NOT_ELIGIBLE:
      case QUALIFICATION_STATUS.REWORK_DISQUALIFIED:
        return "notEligibleMessage";
      case QUALIFICATION_STATUS.MANUALLY_APPROVED:
      case QUALIFICATION_STATUS.MANUALLY_QUALIFIED:
      case QUALIFICATION_STATUS.AUTOMATICALLY_APPROVED:
        return "manuallyApprovedMessage";
      default:
        return "";
    }
  };

  const bannerMessage = (messageKey: string) => {
    const leadershipSeminarMessages = defaultContent.performance.leadershipSeminar as Record<string, string>;
    return (
      <p className="AnnualPerformanceIncome__tileSubMessage">
        {t(`performance.leadershipSeminar.${messageKey}`, leadershipSeminarMessages[messageKey], {
          title: pageTitle,
        })}
      </p>
    );
  };

  const paths = lsPathViewData.Path ?? [];
  const defaultPath = paths.find((p) => p.Default) ?? paths[0];

  if (!lsPathViewData.IsComingSoon && !defaultPath) return null;

  const messageKey = getMessageKey(lsPathViewData.Status, lsPathViewData.AutomaticallyApproved);

  return (
    <PerformanceTile>
      <PerformanceTileHeader link={ROUTE_PATHS.LEADERSHIP_SEMINAR} label={pageTitle} tealiumTag="leadershipSeminar" />
      {lsPathViewData.IsComingSoon ? (
        comingSoonBlock()
      ) : (
        <>
          {messageKey
            ? bannerMessage(messageKey)
            : defaultPath && renderDefaultPath(defaultPath, lsPathViewData.Requirements)}
          <div className="perf-tile__badge">
            <AwardProgressBadge status={parseStatus(lsPathViewData.Status)} hideIcon />
          </div>
        </>
      )}
    </PerformanceTile>
  );
};
